import { useEffect, useRef } from 'react';
import maplibregl, { Map as MlMap, GeoJSONSource } from 'maplibre-gl';
import { buildStyle } from './style';
import { registerChartTransparencyProtocol } from './chartTileTransparency';

// Register the FAA chart-tile grey-pixel transparency protocol once at
// module load. Idempotent and cheap.
registerChartTransparencyProtocol();
import { useShallow } from 'zustand/react/shallow';
import { useUiStore } from '../stores/uiStore';
import { useLayerVisibilityStore } from '../stores/layerVisibilityStore';
import { DIPSITE_OWNERSHIP_COLOR_EXPR } from './dipSitesLayer';
import { useGpsStore } from '../stores/gpsStore';
import { useTrackStore } from '../stores/trackStore';
import { usePlanStore } from '../stores/planStore';

import { fetchBbox, BboxAirport, BboxFix, BboxNavaid } from '../lib/mapBbox';
import { densifyRouteForGreatCircle, geodesicCircleRing } from '../lib/geo';
import { holdingRacetrackCoords } from '../lib/holdingGeometry';
import { rfArcCoords } from '../lib/rfArcGeometry';
import { computeActiveLeg } from '../lib/activeLeg';
import { registerHatchPatterns } from './hatchPatterns';
import { registerProcedureIcons, formatAltitudeLabel } from './procedureIcons';
import { useLayerStatusStore } from '../stores/layerStatusStore';
import { SearchHit } from '../lib/search';
import { ensureAirportIcons, AIRPORT_ICON_EXPRESSION } from './airportIcons';
import { ensurePirepIcons, PIREP_ICON_EXPRESSION } from './pirepIcons';
import { ensureTrafficIcons } from './trafficIcons';
import { ensureWindBarbIcons, WIND_BARB_ICON_EXPRESSION } from './windBarbs';
import { ensureSkyCoverIcons, SKY_COVER_ICON_EXPRESSION } from './skyCoverIcons';
import { ensureNavaidIcons, NAVAID_ICON_EXPRESSION } from './navaidIcons';
import { ensureFixIcons } from './fixIcons';
import { ensureUserWaypointIcons } from './userWaypointIcons';
import { useWaypointStore } from '../stores/waypointStore';
import { ensureFuelIcons } from './fuelIcons';
import { ensureObstacleIcons, OBSTACLE_ICON_EXPRESSION } from './obstacleIcons';
import { ensureRepeaterIcons, REPEATER_ICON_EXPRESSION } from './repeaterIcons';
import {
  addAirspaceExtrusion,
  removeAirspaceExtrusion,
  setExtrusionVisible,
} from '../3d/airspace3d';
import {
  refreshOverlay,
  pushCachedOverlays as pushCachedOverlaysImpl,
  isOverlayLoaded,
  markOverlayLoaded,
  type OverlayId,
} from './overlayRuntime';
import { computePlanMemoKey } from './planMemoKey';
import { OVERLAY_BY_ID, OVERLAY_SPECS } from './overlaySpecs';
import {
  runStyleRebuildHandlers,
  registerRefreshableOverlay,
  registerStyleRebuild,
  runVisibilityHandlers,
  runBboxRefreshHandlers,
  resetVisibilityState,
} from './layerVisibilityRegistry';
import { addOverlays } from './addOverlays';
import { HAZARD_BY_LAYER, GFA_LAYER_IDS } from './gfaLayerIds';
import {
  applyStationLayerFilter,
} from './mapFilters';
import {
  setupPlanLegRubberBand,
  setupLongPress,
} from './mapInteractions';
import { CHART_OVERLAYS, type ChartOverlayId } from './sources';
import { TrafficController } from './controllers/TrafficController';
import { TrackController, pushTrack } from './controllers/TrackController';
import { IhogRingsController } from './controllers/IhogRingsController';
import { DipSitesController } from './controllers/DipSitesController';
import { AirportDiagramController } from './controllers/AirportDiagramController';
import { NamedPassesController } from './controllers/NamedPassesController';
import { WindParticlesController } from './controllers/WindParticlesController';
import { WindPressureCentersController } from './controllers/WindPressureCentersController';
import { TerrainController } from './controllers/TerrainController';
import { OwnshipController } from './controllers/OwnshipController';
import { useWindParticleDebug } from './wind/useWindParticleDebug';
import { pickNearestPoint, pickNearestPointWithLatLon } from './picking/pickNearestPoint';

/**
 * Past chart-native + this many zoom levels, the chart layer becomes
 * useless mush — it's just upscaled pixels. We use this margin TWO
 * different ways:
 *
 *   1. As the layer-level `maxzoom` on the chart raster layer in
 *      style.ts: past `chart.maxzoom + CHART_OVERZOOM_MARGIN` the
 *      chart doesn't render at all and the imagery shows through.
 *      Helo pilots can keep zooming in to spot a helipad on the
 *      satellite without the blurry sectional ruining the picture.
 *
 *   2. As the soft hint for our `maxZoomFor()` clamp — we used to
 *      cap the map's max zoom at `chart.maxzoom + 2` so the user
 *      couldn't push past it. We DON'T do that anymore: the layer-
 *      level maxzoom inside the style now handles the chart
 *      gracefully (it just stops rendering), so we let the user
 *      zoom all the way in to imagery's max. Boss runs helo work
 *      and needs the close-in zoom.
 */
const CHART_OVERZOOM_MARGIN = 2;

function maxZoomFor(_chartId: ChartOverlayId, imageryMaxZoom = 19): number {
  // Always allow zoom up to the imagery's native max. The chart
  // layer's own maxzoom (set in style.ts) makes it disappear past
  // the useful range so the user keeps a crisp imagery view.
  return imageryMaxZoom;
}

export const EMPTY_FC = { type: 'FeatureCollection' as const, features: [] };

// mapInstance moved to src/map/mapInstance.ts (Phase 7 / HI-2').
// This module-level variable is kept for backward-compat with the
// many call sites that already reference mapInstance directly in the
// module scope below (e.g., refreshTfrs, refreshWxCams, etc.).
import { setMapInstance, clearMapInstance, safeGetSource as _safeGetSource } from './mapInstance';
export { getMapInstance } from './mapInstance';
// Re-export safeGetSource and keep it accessible locally.
export { safeGetSource } from './mapInstance';
const safeGetSource = _safeGetSource;
let mapInstance: MlMap | null = null;

// Layer caches + lazy-load flags for the 10 GeoJSON overlays live in
// src/map/overlayRuntime.ts (keyed by OverlayId). isOverlayLoaded() /
// markOverlayLoaded() / refreshOverlay() / getCachedOverlay() are the
// public API; per-overlay URL builders are in src/map/overlaySpecs.ts.
//
// Heliports + wxcams are NOT in that runtime — they're structurally
// different (z≥10 gate + manual feature mapping). They keep their
// own module-level state below.
let heliportsLoaded = false;
let wxCamsLoaded = false;

// HAZARD_BY_LAYER and GFA_LAYER_IDS moved to gfaLayerIds.ts (HI-2 step 2d
// cold-boot fix) to break a circular import that caused a TDZ ReferenceError.
// Imported at the top of this file; re-exported here for back-compat with
// any consumers that import from MapView.
export { HAZARD_BY_LAYER, GFA_LAYER_IDS };

// Filter / tile-URL helpers extracted to src/map/mapFilters.ts.
//
// applyFlightCatFilter is the one MapView-local helper that's a
// thin wrapper over the shared applyStationLayerFilter (it just
// hardcodes the three flightcat layer ids the wrapper needs).
export function applyFlightCatFilter(map: MlMap, offsetH: number): void {
  applyStationLayerFilter(
    map,
    ['flightcat-circle-bg', 'flightcat-circle', 'flightcat-label'],
    offsetH,
  );
}
// getMapInstance and safeGetSource are now exported from ./mapInstance
// (re-exported above for backward compatibility).

export function recenter(lon: number, lat: number, zoom?: number) {
  if (!mapInstance) return;
  mapInstance.flyTo({ center: [lon, lat], zoom: zoom ?? Math.max(mapInstance.getZoom(), 9) });
}

/**
 * Pick clickable features (airports / fixes / navaids) under a tap.
 * Returns deduped SearchHits sorted by distance from the tap, nearest
 * first. Empty array when nothing's in range.
 *
 * `radiusPx` is the half-side of the query box around the tap point; on
 * a touch screen we want something fingertip-sized (~18 px maps to a
 * comfortable 36 px target). Desktop mouse can use the same value
 * without harm — the dedupe keeps the list tight.
 */
/**
 * Query airspace polygons at a screen point. Returns the GeoJSON
 * properties of every SUA / TFR / AIRMET / SIGMET feature that
 * contains the point. Used by the long-press picker.
 */
export function queryAirspaceAt(
  point: { x: number; y: number },
): Array<{ kind: 'sua' | 'tfr' | 'airmet' | 'sigmet' | 'gfa' | 'cwa' | 'dispatch' | 'classBCD' | 'airways'; properties: Record<string, unknown> }> {
  const m = mapInstance;
  if (!m) return [];
  // Order matters: deeper polygon kinds first so denser hazard layers
  // bubble up in the picker. GFA layers are listed individually so the
  // hazard's per-layer visibility (driven by the user's toggles) is
  // honored — if a layer's visibility is 'none', queryRenderedFeatures
  // skips it.
  const layerKindMap: Array<[string, 'sua' | 'tfr' | 'airmet' | 'sigmet' | 'gfa' | 'cwa' | 'dispatch' | 'classBCD' | 'airways']> = [
    ['classBCD-fill', 'classBCD'],
    ['sua-fill', 'sua'],
    ['tfr-fill', 'tfr'],
    ['airmet-fill', 'airmet'],
    ['sigmet-fill', 'sigmet'],
    ['gfa-ifr-fill', 'gfa'],
    ['gfa-mtnobscn-fill', 'gfa'],
    ['gfa-ice-fill', 'gfa'],
    ['gfa-turblo-fill', 'gfa'],
    ['gfa-turbhi-fill', 'gfa'],
    ['gfa-llws-fill', 'gfa'],
    ['gfa-sfcwnd-fill', 'gfa'],
    // FZLVL is line-only — picker reads it via queryRenderedFeatures
    // against the line layer.
    ['gfa-fzlvl-line', 'gfa'],
    ['cwa-fill', 'cwa'],
    // NIFC dispatch zones — deepest in the stack so other airspace
    // hazards still pick first when stacked at the same point.
    ['dispatch-fill', 'dispatch'],
    // Airways centerlines — line-only tap target.
    ['airways-line', 'airways'],
  ];
  const out: Array<{ kind: 'sua' | 'tfr' | 'airmet' | 'sigmet' | 'gfa' | 'cwa' | 'dispatch' | 'classBCD' | 'airways'; properties: Record<string, unknown> }> = [];
  const seen = new Set<string>();
  for (const [layerId, kind] of layerKindMap) {
    if (!m.getLayer(layerId)) continue;
    const fs = m.queryRenderedFeatures([point.x, point.y], { layers: [layerId] });
    for (const f of fs) {
      const props = (f.properties ?? {}) as Record<string, unknown>;
      // De-dupe by a feature signature so a polygon split across tiles
      // doesn't double-render in the picker.
      const sig = kind + ':' + JSON.stringify(props).slice(0, 200);
      if (seen.has(sig)) continue;
      seen.add(sig);
      out.push({ kind, properties: props });
    }
  }
  return out;
}

/**
 * Query for a FAA WeatherCams icon at the given screen point. Returns
 * the camera-site metadata if a tap landed on one, else null. Kept
 * separate from pickFeaturesAt because wxcams aren't 'SearchHit'-shaped
 * (they have a camId instead of an ident-based detail flow).
 */
/** Query for a PIREP icon at the given screen point. Returns the
 *  full PIREP feature properties when a tap landed on one, else null.
 *  Independent from pickFeaturesAt because PIREPs flow through their
 *  own slide-over detail card rather than the airport/navaid path. */
export function pickPirepAt(
  point: { x: number; y: number },
  radiusPx = 18,
): Record<string, unknown> | null {
  const m = mapInstance;
  if (!m) return null;
  const f = pickNearestPoint(m, 'pireps-icon', point, radiusPx);
  return f ? (f.properties ?? null) as Record<string, unknown> | null : null;
}

export function pickWxCamAt(
  point: { x: number; y: number },
  radiusPx = 18,
): { camId: number; ident: string; name: string; lat: number; lon: number } | null {
  const m = mapInstance;
  if (!m) return null;
  const result = pickNearestPointWithLatLon(m, 'wxcams-icon', point, radiusPx);
  if (!result) return null;
  const camId = Number(result.camId);
  if (!Number.isFinite(camId)) return null;
  return {
    camId,
    ident: String(result.ident || ''),
    name: String(result.name || ''),
    lat: result.lat, lon: result.lon,
  };
}

/** Pick a tall obstacle near the tap point. Returns the feature
 *  properties (id, agl, msl, type, lighting, marking, etc.) plus its
 *  lat/lon so the detail card can display position. */
export function pickObstacleAt(
  point: { x: number; y: number },
  radiusPx = 18,
): (Record<string, unknown> & { lat: number; lon: number }) | null {
  const m = mapInstance;
  if (!m) return null;
  return pickNearestPointWithLatLon(m, 'obstacles-icon', point, radiusPx);
}

/** Pick a NIFC repeater site near the tap point. Returns the feature
 *  properties (site, center, state, primary_channel, center_channels)
 *  plus lat/lon so the detail card can show the site geographically. */
export function pickRepeaterAt(
  point: { x: number; y: number },
  radiusPx = 18,
): (Record<string, unknown> & { lat: number; lon: number }) | null {
  const m = mapInstance;
  if (!m) return null;
  const result = pickNearestPointWithLatLon(m, 'repeaters-icon', point, radiusPx);
  if (!result) return null;
  // queryRenderedFeatures stringifies nested object/array properties.
  // Parse them back so the detail card can read primary_channel / center_channels.
  const props: Record<string, unknown> = { ...result };
  for (const k of ['primary_channel', 'center_channels'] as const) {
    const v = props[k];
    if (typeof v === 'string') {
      try { props[k] = JSON.parse(v); } catch { /* leave as string */ }
    }
  }
  return props as Record<string, unknown> & { lat: number; lon: number };
}

/**
 * Hit-test the traffic-icon layer at a screen point. Returns the
 * properties of the nearest traffic target inside the radius (the
 * properties we packed at push time: icaoHex, callsign, registration,
 * trackDeg, colorClass, source, altText) plus a lat/lon. Returns null
 * when no target is within range.
 */
export function pickTrafficAt(
  point: { x: number; y: number },
  radiusPx = 18,
): (Record<string, unknown> & { lat: number; lon: number }) | null {
  const m = mapInstance;
  if (!m) return null;
  return pickNearestPointWithLatLon(m, 'traffic-icon', point, radiusPx);
}

/** Pick a dip-site at the tap point. Returns the feature properties
 *  (dipSiteId, name, sourceType, ownershipClass, managingAgency,
 *  ownerName, reimbursementStatus) plus lat/lon. Returns null if no
 *  dip-site icon falls within the tap radius. Tap radius is slightly
 *  larger than the icon (24px) so a fingertip on the small circle
 *  still picks it up. */
export function pickDipSiteAt(
  point: { x: number; y: number },
  radiusPx = 24,
): (Record<string, unknown> & { lat: number; lon: number }) | null {
  const m = mapInstance;
  if (!m) return null;
  return pickNearestPointWithLatLon(m, 'dip-sites-circle', point, radiusPx);
}

/** Pick an IHOG-rings ring at the tap point. Returns the incidentId
 *  (and the ring kind / radius) so the caller can navigate to
 *  `/incidents/:id`. Uses the fill layer so a tap anywhere inside
 *  the ring counts. The exclusion ring sits inside the contact ring;
 *  when both fills cover the point we prefer the exclusion ring
 *  (smaller / more operationally significant). */
export function pickIhogRingAt(
  point: { x: number; y: number },
): { incidentId: string; incidentName: string; kind: 'contact' | 'exclusion'; radiusNm: number } | null {
  const m = mapInstance;
  if (!m || !m.getLayer('ihog-rings-fill')) return null;
  const features = m.queryRenderedFeatures([point.x, point.y], { layers: ['ihog-rings-fill'] });
  if (features.length === 0) return null;
  // Prefer the exclusion ring when stacked (it's the inner / tighter
  // one — tapping in the overlap should jump to the harder constraint).
  let pick: typeof features[number] | null = null;
  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>;
    if (props.kind === 'exclusion') { pick = f; break; }
    if (!pick) pick = f;
  }
  if (!pick) return null;
  const props = (pick.properties ?? {}) as Record<string, unknown>;
  const incidentId = String(props.incidentId ?? '').trim();
  if (!incidentId) return null;
  return {
    incidentId,
    incidentName: String(props.incidentName ?? ''),
    kind: (props.kind === 'exclusion' ? 'exclusion' : 'contact'),
    radiusNm: Number(props.radiusNm) || 0,
  };
}

/** Returns the user waypoint feature at the tapped point, or null.
 *  Used by MapPage to open the waypoint detail card. */
export function pickUserWaypointAt(
  point: { x: number; y: number },
  radiusPx = 22,
): { id: string; name: string; lat: number; lon: number; notes?: string } | null {
  const m = mapInstance;
  if (!m) return null;
  const result = pickNearestPointWithLatLon(m, 'user-waypoints-symbol', point, radiusPx);
  if (!result) return null;
  return {
    id: String(result.id ?? ''),
    name: String(result.name ?? ''),
    lat: result.lat,
    lon: result.lon,
    notes: result.notes ? String(result.notes) : undefined,
  };
}

export function pickFeaturesAt(
  point: { x: number; y: number },
  radiusPx = 18,
): SearchHit[] {
  const m = mapInstance;
  if (!m) return [];
  // 'plan-points' is ALWAYS included even when the user-fix layer is
  // toggled off, so a procedure waypoint (FAF / IAF / MAP / stepdown
  // / hold fix) is always clickable while it's on the active route.
  // 2026-05-16: also include heliports-icon so tapping a hospital pad
  // opens the airport-detail card (boss feedback — pads were silent).
  //
  // 2026-05-17: plan-points listed FIRST so an in-plan waypoint wins
  // when stacked with a base airport/fix icon. Tapping the magenta
  // dot should always route through the plan-aware code path so the
  // detail card can offer hold-at-this-waypoint, remove-from-plan,
  // and other plan-context actions. Same ident from a base layer is
  // suppressed by the dedup below — the plan-points hit carries the
  // original kind via `planKind` so we still open the right card.
  const layers = ['plan-points', 'airports-icon', 'fixes-icon', 'waypoints-icon', 'heliports-icon']
    .filter((id) => !!m.getLayer(id));
  if (layers.length === 0) return [];
  const r = Math.max(4, radiusPx);
  const bbox: [maplibregl.PointLike, maplibregl.PointLike] = [
    [point.x - r, point.y - r],
    [point.x + r, point.y + r],
  ];
  const features = m.queryRenderedFeatures(bbox, { layers });
  // Dedup ladder: an in-plan feature (layerId === 'plan-points') wins
  // over the base-layer rendering of the same ident. Because
  // `plan-points` sits first in the `layers` array, MapLibre returns
  // it first in `features` for stacked points. The ident-level Set
  // here then suppresses a redundant `airports-icon:KSTS` row from
  // appearing alongside the in-plan `plan-points:KSTS` row.
  const seenIdent = new Set<string>();
  const hits: Array<SearchHit & { _dist: number; _inPlan?: boolean }> = [];
  for (const f of features) {
    const props = (f.properties ?? {}) as Record<string, unknown>;
    const ident = String(props.ident ?? '').trim();
    if (!ident) continue;
    const geom = f.geometry;
    if (!geom || geom.type !== 'Point') continue;
    const [lon, lat] = (geom as GeoJSON.Point).coordinates as [number, number];
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) continue;
    const layerId = (f.layer && (f.layer as { id?: string }).id) || '';
    // Suppress repeats of the same ident regardless of layer. Because
    // plan-points is first in `layers`, the in-plan version always
    // wins for stacked hits.
    if (seenIdent.has(ident)) continue;
    seenIdent.add(ident);

    const projected = m.project([lon, lat]);
    const dist = Math.hypot(projected.x - point.x, projected.y - point.y);

    let kind: SearchHit['kind'];
    let badge: string | undefined;
    let sub: string | undefined;
    let name: string;
    const typeStr = String(props.type ?? '');
    if (layerId === 'airports-icon') {
      kind = 'airport';
      badge = typeStr === 'large_airport' ? 'apt' : (typeStr === 'seaplane_base' ? 'spb' : 'apt');
      name = String(props.name ?? ident);
      sub = typeStr.replace(/_/g, ' ');
    } else if (layerId === 'heliports-icon') {
      // 2026-05-16: route heliport taps to the full airport detail card.
      // Kind='airport' here on purpose — AirportDetail handles them via
      // /api/airport-nasr?ident=AKxx which returns the heliport's name,
      // facilityType, ops, hospital flag, etc.
      kind = 'airport';
      const isHospital = props.hospital === true;
      badge = isHospital ? 'hosp' : 'heli';
      name = String(props.name ?? ident);
      sub = isHospital ? 'Hospital heliport' : 'Heliport';
    } else if (layerId === 'fixes-icon') {
      kind = 'fix';
      badge = 'fix';
      name = ident; // 5-letter waypoint; ident IS the name
      sub = String(props.cat ?? '') || undefined;
    } else if (layerId === 'plan-points') {
      // Procedure / route waypoint on the active plan.
      // 2026-05-17: route the hit to the correct detail card by
      // reading the original feature kind stamped at source-build
      // time. Without this, an in-plan AIRPORT (KSTS, O46) opened the
      // lightweight DetailPane (which assumes fix/navaid shape)
      // instead of the full AirportDetail sheet.
      const planKindRaw = String(props.planKind ?? '').toLowerCase();
      if (planKindRaw === 'airport') {
        kind = 'airport';
        badge = 'apt';
      } else if (planKindRaw === 'navaid') {
        kind = 'navaid';
        badge = 'VOR';
      } else if (planKindRaw === 'user') {
        kind = 'fix';
        badge = 'usr';
      } else {
        // Procedure / fix point: synthesized helpers are filtered out
        // at source-build time so they never reach the click handler.
        kind = 'fix';
        badge = 'fix';
      }
      name = ident;
      // Mark this hit as in-plan so downstream UI (DetailPane) can
      // offer plan-aware actions like "Add hold at this waypoint".
      hits.push({ kind, ident, name, lat, lon, badge, sub, _dist: 0, _inPlan: true });
      continue;
    } else {
      kind = 'navaid';
      badge = typeStr || 'VOR';
      name = String(props.name ?? ident);
      sub = typeStr || undefined;
    }
    hits.push({ kind, ident, name, lat, lon, badge, sub, _dist: dist });
  }
  // In-plan hits sort to the top regardless of pixel distance — the
  // user explicitly built that point into the route, so it's the
  // intended click target. Within each group (in-plan vs not), keep
  // the existing distance ordering.
  hits.sort((a, b) => {
    if (!!a._inPlan !== !!b._inPlan) return a._inPlan ? -1 : 1;
    return a._dist - b._dist;
  });
  return hits.map(({ _dist: _d, _inPlan: _ip, ...h }) => h);
}

interface Props {
  onMapClick?: (
    lngLat: [number, number],
    point: { x: number; y: number },
  ) => void;
  /**
   * Fires when the user finishes dragging a flight-plan leg to a new
   * location. The map calls this with the source leg index and the
   * drop coordinate; MapPage opens the rubber-band candidate picker
   * (nearest airport / fix / navaid + the raw GPS point) so the user
   * can pick what to insert into that leg.
   */
  onPlanLegDrop?: (
    legIndex: number,
    dropLngLat: [number, number],
    screenPoint: { x: number; y: number },
  ) => void;
  /**
   * Fires after a long-press anywhere on the map (~500ms hold without
   * movement). MapPage opens a picker showing nearby airports /
   * navaids / fixes + airspace at that point. ForeFlight calls it the
   * 'rubber-band drop' menu when used at a point not on the route.
   */
  onLongPress?: (
    lngLat: [number, number],
    screenPoint: { x: number; y: number },
  ) => void;
}

export default function MapView({ onMapClick, onPlanLegDrop, onLongPress }: Props) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MlMap | null>(null);

  // Bumps every time the basemap style finishes rebuilding. Layer-
  // visibility effects include this in their dep array so they
  // reapply visibility against the freshly-recreated MapLibre
  // layers — otherwise switching the chart layer blanks out every
  // opt-in overlay (classBCD, airways, fixes, heliports, etc.)
  // until the user toggles it off + on. (Aaron field report
  // 2026-05-13 23:39 PT.)
  // Note: styleEpoch removed — basemap switch restoration is now handled
  // entirely by runStyleRebuildHandlers() in the styledata callback.

  // Keep the latest onMapClick in a ref so the MapLibre 'click' handler
  // (registered once at mount) always sees the current closure. Without
  // this, tap-to-add-waypoint never fires after the user toggles it on,
  // because the handler is closed over the initial `onMapClick = undefined`
  // (or the first render's tap-disabled version).
  const onMapClickRef = useRef<typeof onMapClick>(onMapClick);
  useEffect(() => { onMapClickRef.current = onMapClick; }, [onMapClick]);
  // Same ref dance for the rubber-band drag callback.
  const onPlanLegDropRef = useRef<typeof onPlanLegDrop>(onPlanLegDrop);
  useEffect(() => { onPlanLegDropRef.current = onPlanLegDrop; }, [onPlanLegDrop]);
  // Same for long-press.
  const onLongPressRef = useRef<typeof onLongPress>(onLongPress);
  useEffect(() => { onLongPressRef.current = onLongPress; }, [onLongPress]);

  // Wind particles dev flag (?windDebug=1). No-op without the URL flag.
  // Removed when commit 3 ships the real wind controller + UI.
  useWindParticleDebug();

  // HI-1' (2026-05-24): Single shallow read of all layer-visibility flags.
  // runVisibilityHandlers() reads the same snapshot when called from the
  // ONE visibility useEffect below.
  const layers = useLayerVisibilityStore(useShallow((s) => s.layers));
  const baseImagery = useUiStore((s) => s.baseImagery);
  const chartOverlay = useUiStore((s) => s.chartOverlay);
  const chartOpacity = useUiStore((s) => s.chartOpacity);
  // Time-slider values are included as deps in the ONE visibility useEffect
  // so that applyState functions (which call getState() at call time)
  // re-run when the user drags any time-scrubber slider.
  const wxHazardOffsetH = useUiStore((s) => s.wxHazardOffsetH);
  const nexradOffsetMin = useUiStore((s) => s.nexradOffsetMin);
  const nexradVariant   = useUiStore((s) => s.nexradVariant);
  const sfcAnalysisOffsetH = useUiStore((s) => s.sfcAnalysisOffsetH);
  const smokeOffsetH = useUiStore((s) => s.smokeOffsetH);
  // Phase 3: smoke layer reads altitudeSliderFt to pick the closest
  // published hybrid level. Include in the visibility-effect deps so
  // dragging the slider re-fires applyState (which fires updateImage).
  const altitudeSliderFt = useUiStore((s) => s.altitudeSliderFt);
  // heliportsHospitalsOnly needs a refresh when it changes (heliports
  // re-fetches with the new hospital-only filter).
  const heliportsHospitalsOnly = useUiStore((s) => s.heliportsHospitalsOnly);
  // enable3d is still needed for the 3D-airspace effect.
  const enable3d = useUiStore((s) => s.enable3d);

  // ---------- init ----------
  useEffect(() => {
    if (!containerRef.current) return;

    const map = new maplibregl.Map({
      container: containerRef.current,
      style: buildStyle(baseImagery, chartOverlay, chartOpacity),
      center: [-122.42, 41.73], // KSIY-ish, Northern California
      zoom: 7,
      maxZoom: maxZoomFor(chartOverlay),
      attributionControl: { compact: true },
      pitchWithRotate: false,
      dragRotate: false,
      // Antialiased canvas — required for crisp edges on the globe
      // sphere when zoomed out. MapLibre 5+ feature; benign on flat
      // mercator at high zoom.
      canvasContextAttributes: { antialias: true },
    });
    map.touchZoomRotate.disableRotation();

    // WebGL context-loss handlers (audit 2026-05-22 CR-1 / Claude H9).
    // iOS Safari aggressively recycles WebGL contexts under memory
    // pressure or after backgrounding. Without these handlers the map
    // goes blank mid-flight and the only recovery is a full reload —
    // unacceptable in a moving cockpit. We log the loss, prevent the
    // default (which would prevent restoration), and let MapLibre
    // re-initialize on the restored event.
    try {
      const canvas = map.getCanvas();
      if (canvas) {
        canvas.addEventListener('webglcontextlost', (e: Event) => {
          // eslint-disable-next-line no-console
          console.warn('[MapView] WebGL context lost — attempting to recover');
          e.preventDefault();
        }, false);
        canvas.addEventListener('webglcontextrestored', () => {
          // eslint-disable-next-line no-console
          console.info('[MapView] WebGL context restored');
          // MapLibre 5 reinstalls its style/sources/layers automatically
          // when the context is restored; custom layers (wind particles,
          // wind fill) get re-mounted via the styledata listener that
          // already exists in WindParticlesController.
          try { map.triggerRepaint(); } catch { /* ignore */ }
        }, false);
      }
    } catch { /* ignore — canvas API not available */ }

    // Note: we intentionally do NOT add a NavigationControl. The default
    // MapLibre zoom +/- buttons sit in the top-right corner where they
    // collide with the LayerSheet button + waste real estate on mobile.
    // Pinch-to-zoom + double-tap is universal on touch; desktop users
    // get scroll-wheel zoom. (Boss feedback 2026-05-10.)
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'nautical' }), 'bottom-right');

    // Globe projection is baked into the style spec via
    // buildStyle(...).projection so it survives setStyle() calls
    // without us having to re-call setProjection() on every chart/
    // imagery toggle. See style.ts for the comment about the
    // MapLibre 5.24 worker-arg race condition that approach avoids.

    map.on('load', () => {
      registerHatchPatterns(map);
      void registerProcedureIcons(map);
      addOverlays(map);
      // Cold-boot restore: push IDB-cached overlay data into the freshly-
      // created sources (same as the setStyle/styledata path does after a
      // basemap switch). Without this, overlay sources start with EMPTY_FC
      // even when the user had layers ON before reload, because the
      // per-layer React useEffects already ran at component mount time
      // (before the map loaded) and returned early — so no data was ever
      // seeded.
      pushCachedOverlays(map);
      // Seed the isOverlayLoaded flags AND trigger initial data fetches for
      // layers that are already ON from persisted state. Without this:
      //   1. refreshBboxLayers (below) skips SUA/VIIRS/etc. because
      //      isOverlayLoaded returns false.
      //   2. Nationwide layers (AIRMET/SIGMET/CWA/GFA) are never fetched
      //      because their lazy-load useEffects ran before the map loaded
      //      and returned early. pushCachedOverlays() may have restored
      //      IDB data, but on first load or if IDB is stale a fresh fetch
      //      is needed.
      // The isOverlayLoaded flag gates the per-moveend refresh loop so
      // seeding it here means subsequent pans also keep the data fresh.
      {
        const _lv = useLayerVisibilityStore.getState().layers;
        // Bbox-filtered heavy overlays — refreshBboxLayers handles the fetch
        // once the flag is set.
        if (_lv.sua     && !isOverlayLoaded('sua'))      markOverlayLoaded('sua');
        if (_lv.viirs   && !isOverlayLoaded('viirs'))    markOverlayLoaded('viirs');
        if (_lv.pireps  && !isOverlayLoaded('pireps'))   markOverlayLoaded('pireps');
        if (_lv.flightcat && !isOverlayLoaded('flightcat')) markOverlayLoaded('flightcat');
        if (_lv.classBCD && !isOverlayLoaded('classBCD')) markOverlayLoaded('classBCD');
        if (_lv.airways  && !isOverlayLoaded('airways'))  markOverlayLoaded('airways');
        if (_lv.obstacles && !isOverlayLoaded('obstacles')) markOverlayLoaded('obstacles');
        if (_lv.dispatch  && !isOverlayLoaded('dispatch'))  markOverlayLoaded('dispatch');
        // Nationwide wx layers: ALWAYS force-fetch when ON at cold-boot.
        // The React visibility effect (HI-1') may have already set
        // isOverlayLoaded before sources existed, leaving the source empty.
        // Using markOverlayLoaded(id, true) + unconditional fetch ensures
        // data arrives even if the guard was pre-tripped.
        if (_lv.wxhazards && _lv.airmet)  { markOverlayLoaded('airmet',  true); void refreshAirmets(map); }
        if (_lv.wxhazards && _lv.sigmet)  { markOverlayLoaded('sigmet',  true); void refreshSigmets(map); }
        if (_lv.wxhazards && _lv.cwa)     { markOverlayLoaded('cwa',     true); void refreshCwa(map); }
        const _anyGfa = _lv.wxhazards && (
          _lv['gfa-ifr'] || _lv['gfa-mtnobscn'] || _lv['gfa-ice'] ||
          _lv['gfa-turblo'] || _lv['gfa-turbhi'] || _lv['gfa-llws'] ||
          _lv['gfa-fzlvl'] || _lv['gfa-sfcwnd']
        );
        if (_anyGfa) { markOverlayLoaded('gfa', true); void refreshGfa(map); }
      }
      // First-paint: seed flight-plan + breadcrumb track.
      pushPlan(map);
      pushTrack(map);
      // Restore all registered layer families (mount + applyState).
      // This is the single canonical restore call — it replaces the
      // old hand-listed ref-call chain.
      runStyleRebuildHandlers(map);
      // Ensure plan layers are always on top.
      raisePlanLayersToTop(map);
      // First-paint bbox refresh. runBboxRefreshHandlers handles the
      // registry families; refreshBboxCoreFeatures handles airports/
      // fixes/waypoints/heliports which use the bboxAbort pattern.
      refreshTfrs(map).catch(() => undefined);
      void refreshDispatch(map);
      void refreshRepeaters(map);
      const _bboxB = map.getBounds();
      const _bbox = { w: _bboxB.getWest(), s: _bboxB.getSouth(), e: _bboxB.getEast(), n: _bboxB.getNorth() };
      void runBboxRefreshHandlers(map, _bbox, Math.round(map.getZoom()));
      void refreshBboxCoreFeatures(map, Math.round(map.getZoom()));
    });
    let moveendTimer: number | null = null;
    let lastBboxKey: string | null = null;
    map.on('moveend', () => {
      // F1 (2026-05-16 perf audit): skip the bbox-refresh cascade for
      // programmatic camera moves triggered by follow-ownship. Before this
      // gate, every GPS fix → easeTo → moveend → 12-fetch bbox refresh
      // fired ~once/700ms during cruise, dominating the main thread.
      if ((map as unknown as { __fyProgrammingMove?: boolean }).__fyProgrammingMove) return;
      // F4 (2026-05-16 perf audit): bbox-quantize gate. Skip the refresh
      // when the viewport has only changed at sub-bucket resolution. Same
      // pattern as HoldingsMapLayer.quantize() — 0.5° / 1-zoom buckets.
      const b = map.getBounds();
      const key = [
        Math.floor(b.getWest()  / 0.5) * 0.5,
        Math.floor(b.getSouth() / 0.5) * 0.5,
        Math.ceil(b.getEast()   / 0.5) * 0.5,
        Math.ceil(b.getNorth()  / 0.5) * 0.5,
        Math.round(map.getZoom()),
      ].join('|');
      if (key === lastBboxKey) return;
      lastBboxKey = key;
      if (moveendTimer != null) window.clearTimeout(moveendTimer);
      moveendTimer = window.setTimeout(() => {
        moveendTimer = null;
        const bb = map.getBounds();
        const bbox = { w: bb.getWest(), s: bb.getSouth(), e: bb.getEast(), n: bb.getNorth() };
        const z = Math.round(map.getZoom());
        void runBboxRefreshHandlers(map, bbox, z);
        void refreshBboxCoreFeatures(map, z);
      }, 200);
    });

    // Refresh cadences:
    //   - TFRs: every 5 min (live operational data, ~90KB nationwide so still no bbox)
    //   - NIFC fires: every 15 min, bbox-filtered via refreshBboxLayers
    //   - SUA: NOT refreshed during a session (56-day cycle, bbox-filtered, cached 24h)
    const tfrTimer = setInterval(() => { void refreshTfrs(map); }, 5 * 60 * 1000);
    const fireTimer = setInterval(() => { void refreshFiresInBbox(map); }, 15 * 60 * 1000);
    (map as any).__fyTfrTimer = tfrTimer;
    (map as any).__fyFireTimer = fireTimer;

    // Always-on listener that defers to the latest callback via the ref.
    // Reading from the ref dodges the stale-closure pitfall.
    map.on('click', (e) => {
      // Skip the click if the same gesture just fired a long-press;
      // otherwise both menus open back-to-back.
      if (longPressGuard.wasLongPressJustFired()) return;
      const cb = onMapClickRef.current;
      if (cb) cb([e.lngLat.lng, e.lngLat.lat], { x: e.point.x, y: e.point.y });
    });

    // ---------- Rubber-band route-leg drag ----------
    // ForeFlight-style: grab a flight-plan leg line and drag it to a
    // new position. On drop we surface a small candidate picker (raw
    // GPS coord + nearby airports/fixes/navaids) and the user picks
    // what to insert as a new waypoint into that leg.
    //
    // The drag is implemented by hand because MapLibre doesn't have
    // first-class feature-drag; we listen for `mousedown` on the
    // `plan-line` layer, capture document-level move/up to follow
    // the pointer, and disable map pan during the drag so the gesture
    // doesn't double up.
    setupPlanLegRubberBand(map, () => onPlanLegDropRef.current);
    const longPressGuard = setupLongPress(map, () => onLongPressRef.current);

    mapRef.current = map;
    mapInstance = map;
    setMapInstance(map);
    // Diagnostic: re-expose during active route-line debugging.
    (window as any).__fyMap = map;
    return () => {
      const t = (map as any).__fyTfrTimer;
      const f = (map as any).__fyFireTimer;
      if (t) clearInterval(t);
      if (f) clearInterval(f);
      resetVisibilityState();
      map.remove();
      mapRef.current = null;
      if (mapInstance === map) mapInstance = null;
      clearMapInstance(map);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- base layer change ----------
  // setStyle() wipes overlays. We re-add them, then push the data we've
  // already fetched (no upstream re-pull on style toggle — saves bandwidth
  // and keeps mobile responsive). Also re-clamp maxZoom so the user
  // can't zoom past the new chart's useful resolution.
  //
  // (Codex audit 2026-05-18 finding 3) Skip the very first run. The map
  // constructor already takes buildStyle(...) as its initial style; this
  // effect used to fire on mount too, wiping that initial style, re-
  // adding every overlay, and forcing a second refreshBboxLayers cycle.
  // didApplyInitialStyle gates the first invocation so cold-start work
  // happens exactly once.
  const didApplyInitialStyle = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!didApplyInitialStyle.current) {
      didApplyInitialStyle.current = true;
      return;
    }
    map.setStyle(buildStyle(baseImagery, chartOverlay, chartOpacity));
    const newMax = maxZoomFor(chartOverlay);
    map.setMaxZoom(newMax);
    if (map.getZoom() > newMax) map.setZoom(newMax);
    map.once('styledata', () => {
      registerHatchPatterns(map);
      void registerProcedureIcons(map);
      addOverlays(map);
      raisePlanLayersToTop(map);
      // Re-push cached overlay data into the freshly-rebuilt sources.
      // pushCachedOverlays handles fires/sua/tfr; bbox layers re-fetch.
      pushCachedOverlays(map);
      // F10: basemap swap nukes the 'plan' source contents; reset the
      // memo so the next pushPlan() actually writes setData even though
      // the route content is unchanged.
      invalidatePushPlanMemo();
      pushPlan(map);
      pushTrack(map);
      void refreshDispatch(map);
      void refreshRepeaters(map);
      // Run all registered layer-family restores (mount + applyState)
      // to bring every overlay back to its pre-switch state.
      runStyleRebuildHandlers(map);
      // Refresh bbox layers after basemap switch.
      const _bboxBs = map.getBounds();
      const _bboxS = { w: _bboxBs.getWest(), s: _bboxBs.getSouth(), e: _bboxBs.getEast(), n: _bboxBs.getNorth() };
      void runBboxRefreshHandlers(map, _bboxS, Math.round(map.getZoom()));
      void refreshBboxCoreFeatures(map, Math.round(map.getZoom()));
    });
    // chartOpacity is captured here so a style-rebuild gets the latest
    // value, but we don't list it as an effect dep — a stand-alone
    // effect below applies opacity changes via setPaintProperty
    // without rebuilding the style.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseImagery, chartOverlay]);

  // ---------- chart opacity (cheap path) ----------
  // setPaintProperty avoids the full setStyle rebuild so dragging the
  // opacity slider is buttery. The chart layer uses an interpolate
  // expression so it auto-fades as the user zooms past chart-native;
  // we rebuild the same expression here keyed on whatever opacity the
  // slider ended up at, so dragging the slider down at z=8 stays
  // applied at z=14 too.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (!map.getLayer('chart')) return;
    // Mirror the safety clamp from style.ts so a slider at 1.0 still
    // produces a visible imagery layer beneath. See style.ts for why.
    const safe = Math.max(0, Math.min(0.92, chartOpacity));
    const def = chartOverlay !== 'none' ? CHART_OVERLAYS[chartOverlay] : null;
    const native = def?.maxzoom ?? 12;
    const fadeOut = native + 3;
    map.setPaintProperty('chart', 'raster-opacity', [
      'interpolate', ['linear'], ['zoom'],
      0,        safe,
      native,   safe,
      fadeOut,  0,
    ] as unknown as number);
  }, [chartOpacity, chartOverlay]);

  // ---------- overlay visibility ── HI-1' (2026-05-24) ----------

  // ONE visibility effect replaces ~50 per-layer effects.
  // runVisibilityHandlers iterates all registered layer families that
  // have a visibilitySelector, calls their applyState (which handles
  // visibility + filters), and triggers onToggleOn on OFF→ON transition.
  //
  // Dep list includes time-slider values so that filter-bearing
  // applyState functions (nexrad, sfcanalysis, gfa, sigmet, cwa,
  // wxstations) re-run when the user drags any slider.
  // NOTE: styleEpoch removed — basemap-rebuild restoration is now
  // owned by runStyleRebuildHandlers() inside the styledata callback.
  // THE ONE VISIBILITY USEEFFECT (HI-1' Phase 3)
  // Replaces the ~50 per-layer visibility effects that lived here.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    runVisibilityHandlers(map, layers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layers, wxHazardOffsetH, nexradOffsetMin, nexradVariant, sfcAnalysisOffsetH, smokeOffsetH, altitudeSliderFt]);

  // Small heliports-specific effect: refresh when hospital-only filter
  // changes (visibility doesn't change, just the data filter changes).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !layers.heliports) return;
    void refreshHeliportsInBbox(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [heliportsHospitalsOnly]);

  // ---- The ~50 individual per-layer visibility effects have been
  // removed (HI-1' Phase 3). They are replaced by the ONE useEffect
  // above calling runVisibilityHandlers(). Each layer module now owns
  // its visibility in its applyState / visibilitySelector.
  //
  // Removed effects (in original order):
  //   pairs table (styleEpoch), fires, waypoints, fixes, heliports,
  //   dispatch, obstacles, repeaters, wxcams, sua, airports, classBCD,
  //   airways, viirs, airmet, sigmet, cwa, flightcat, surfwinds,
  //   ceilvis, temperature, dewspread, anyStationLayerOn fetch trigger,
  //   pireps, nexrad+scrubber, sfcanalysis+scrubber, satgeocolor,
  //   satir, terrain+rAF+ramp, fuel100ll, fueljeta, gfa-chips,
  //   gfa-timescrubber, tfr, flightPlan.
  //
  // Terrain + ownship effects extracted to TerrainController +
  // OwnshipController respectively.

  // ---------- live data subscriptions ----------
  // Plan
  useEffect(() => {
    // Subscribe to plan-store changes. Source updates fire immediately on
    // every store change so the line stays in sync with the route data.
    //
    // fitMapToRoute is debounced by 300ms because three rapid sequential
    // add() calls (e.g. bulk paste of 'KSIY KMFR KSFO') would otherwise
    // fire three back-to-back fitBounds animations within ~10ms. Debouncing
    // means we fit once, after the user is done adding waypoints.
    //
    // CRITICAL: do NOT do an 'empty FC then real data' kick after the
    // fit completes. A diagnostic sub-agent traced that pattern as the
    // direct cause of CANCELED tile requests — specifically, tile
    // (z=7, x=20, y=47) covering the KSIY/KMFR latitude band gets its
    // load canceled by the empty setData, leaving that tile rendered
    // without the line. The simple direct path (pushPlan + debounced
    // fitBounds, nothing else) renders the full line correctly.
    let prevRouteLen = usePlanStore.getState().route.length;
    let fitTimer: number | null = null;
    const unsub = usePlanStore.subscribe((state) => {
      const map = mapRef.current;
      if (!map) return;
      pushPlan(map);
      const newLen = state.route.length;
      if (newLen >= 2 && newLen !== prevRouteLen) {
        if (fitTimer != null) window.clearTimeout(fitTimer);
        fitTimer = window.setTimeout(() => {
          fitTimer = null;
          const m = mapRef.current;
          if (!m) return;
          fitMapToRoute(m, usePlanStore.getState().route);
          // After the camera animation completes, re-push the plan data.
          // MapLibre's GeoJSON source tessellates per-tile lazily; tiles
          // that came into view DURING the fitBounds animation may have
          // been tessellated against an in-flight intermediate state.
          // A second setData with the final data forces fresh
          // tessellation across every visible tile after the camera
          // settles. (The trick is NOT to setData(empty) in between
          // — that cancels in-flight tile loads, leaving them blank.)
          m.once('moveend', () => {
            // Plain re-push, no empty intermediate.
            const m2 = mapRef.current;
            if (m2) pushPlan(m2);
          });
        }, 300);
      }
      prevRouteLen = newLen;
    });
    // Active-leg color tracking: re-run pushPlan whenever the active
    // leg index changes (advances to the next waypoint) so the orange
    // 'currently flying' segment migrates with the aircraft. We only
    // re-push on index changes, NOT on every GPS fix (which fires at
    // ~1 Hz), to keep the GeoJSON tessellation cheap.
    let lastActiveLegIndex = -2;
    const unsubGpsActiveLeg = useGpsStore.subscribe((state) => {
      const map = mapRef.current;
      if (!map) return;
      const route = usePlanStore.getState().route;
      if (route.length < 2) return;
      const { activeLegIndex } = computeActiveLeg(state.fix, route);
      if (activeLegIndex !== lastActiveLegIndex) {
        lastActiveLegIndex = activeLegIndex;
        pushPlan(map);
      }
    });
    return () => {
      if (fitTimer != null) window.clearTimeout(fitTimer);
      unsub();
      unsubGpsActiveLeg();
    };
  }, []);

  // (Breadcrumb track + range rings extracted to <TrackController /> —
  //  see src/map/controllers/TrackController.tsx)

  // 3D airspace + TFR + SUA volumes via MapLibre fill-extrusion.
  //
  // Day-1 spike 2026-05-16. Boss approved as a kicked-tires feature behind
  // the `enable3d` flag (off by default). When ON, fill-extrusion layers
  // are added on top of the existing classBCD / tfr / sua sources; toggle
  // OFF strips them. Easy to remove by deleting src/3d/ and this effect.
  //
  // The visibility/data binding to each underlying source is unchanged —
  // the user's 2D Class B/C/D / TFR / SUA toggles still control whether
  // the source has data. The extrusion layers just project that data
  // upward when the map is pitched.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const apply = () => {
      if (enable3d) {
        addAirspaceExtrusion(map);
        setExtrusionVisible(map, true);
      } else {
        removeAirspaceExtrusion(map);
      }
    };
    // Defer until the style + base sources are loaded so addAirspaceExtrusion
    // doesn't no-op against a not-yet-registered source.
    if (map.isStyleLoaded() && map.getSource('classBCD')) {
      apply();
    } else {
      const onIdle = () => {
        if (map.getSource('classBCD') || map.getSource('tfr') || map.getSource('sua')) {
          apply();
          map.off('idle', onIdle);
        }
      };
      map.on('idle', onIdle);
      return () => {
        map.off('idle', onIdle);
        if (enable3d) removeAirspaceExtrusion(map);
      };
    }
    return () => {
      if (enable3d) removeAirspaceExtrusion(map);
    };
  }, [enable3d]);




  // (ADS-B traffic pipeline extracted to <TrafficController /> —
  //  see src/map/controllers/TrafficController.tsx)

  // Bbox-driven map dots are pushed via fetchBbox in refreshBboxLayers()
  // — fired on map load, base layer change, and debounced moveend.

  // User waypoints — push store changes into the map source so markers stay in sync.
  useEffect(() => {
    function push() {
      const map = mapRef.current;
      if (!map) return;
      const src = map.getSource('user-waypoints') as GeoJSONSource | undefined;
      if (!src) return;
      const wpts = useWaypointStore.getState().list;
      src.setData({
        type: 'FeatureCollection',
        features: wpts.map((w) => ({
          type: 'Feature',
          geometry: { type: 'Point', coordinates: [w.lon, w.lat] },
          properties: { id: w.id, name: w.name, notes: w.notes ?? null },
        })),
      });
    }
    // Push immediately on mount + subscribe to future changes.
    // HI-1 step 3: registered with the central style-rebuild registry.
    // MapView's styledata + map.on('load') handlers restore us via
    // runStyleRebuildHandlers(). The prior waypointsPushRef ref is gone.
    const unregisterRebuild = registerStyleRebuild({
      id: 'waypoints',
      restore: () => push(),
    });
    push();
    const unsub = useWaypointStore.subscribe(() => push());
    return () => {
      unsub();
      unregisterRebuild();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps


  return (
    <>
      <TrafficController />
      <TrackController />
      <IhogRingsController />
      <DipSitesController />
      <AirportDiagramController />
      <NamedPassesController />
      <WindParticlesController />
      {/* H/L pressure-center labels — coupled to Wind layer visibility.
          Data from manifest qa.lowCenters / qa.highCenters (2026-05-28). */}
      <WindPressureCentersController />
      {/* HI-2' (2026-05-24): extracted from MapView */}
      <TerrainController />
      <OwnshipController />
      <div
        ref={containerRef}
        className="absolute inset-0"
        style={{ width: '100%', height: '100%' }}
      />
    </>
  );
}

// ---------- helpers ----------
// HI-1 step 4: addOverlays() moved to src/map/addOverlays.ts (see import above).

/**
 * Re-push cached overlay data into the (recreated) sources after a style
 * change. No network. Called from the styledata handler when basemap
 * switches between VFR / IFR / topo.
 */
function pushCachedOverlays(map: MlMap) {
  pushCachedOverlaysImpl(map, OVERLAY_SPECS);
}

function currentBboxString(map: MlMap): string {
  const b = map.getBounds();
  return [b.getWest(), b.getSouth(), b.getEast(), b.getNorth()]
    .map((n) => n.toFixed(4))
    .join(',');
}

// --- GeoJSON overlay refreshers -------------------------------------
//
// All 10 GeoJSON overlay refreshers (fires/tfr/sua/viirs/airmet/sigmet/
// gfa/cwa/flightcat/pireps) used to be hand-written 14-line copies.
// They now delegate to overlayRuntime.refreshOverlay(map, spec), with
// per-overlay spec lookup keyed by OverlayId. The named wrappers below
// keep the existing module call sites (refreshFiresInBbox, refreshTfrs,
// refreshGfaPublic, etc.) working without churn.

function refreshById(id: OverlayId, map?: MlMap | null) {
  const m = map ?? mapInstance;
  if (!m) return Promise.resolve(null);
  const spec = OVERLAY_BY_ID.get(id);
  if (!spec) return Promise.resolve(null);
  return refreshOverlay(m, spec);
}

async function refreshFiresInBbox(map?: MlMap | null) { await refreshById('fires', map); }
export async function refreshTfrs(map?: MlMap | null) { await refreshById('tfr', map); }
async function refreshSuaInBbox(map?: MlMap | null) { await refreshById('sua', map); }
async function refreshViirsInBbox(map?: MlMap | null) { await refreshById('viirs', map); }
async function refreshAirmets(map?: MlMap | null) { await refreshById('airmet', map); }
async function refreshSigmets(map?: MlMap | null) { await refreshById('sigmet', map); }
async function refreshGfa(map?: MlMap | null) { await refreshById('gfa', map); }
async function refreshCwa(map?: MlMap | null) { await refreshById('cwa', map); }
async function refreshFlightCat(map?: MlMap | null) { await refreshById('flightcat', map); }
async function refreshPireps(map?: MlMap | null) { await refreshById('pireps', map); }
async function refreshDispatch(map?: MlMap | null) { await refreshById('dispatch', map); }
async function refreshObstacles(map?: MlMap | null) { await refreshById('obstacles', map); }
async function refreshRepeaters(map?: MlMap | null) { await refreshById('repeaters', map); }
async function refreshClassBCDInBbox(map?: MlMap | null) { await refreshById('classBCD', map); }
async function refreshAirwaysInBbox(map?: MlMap | null) { await refreshById('airways', map); }

// Public LayerSheet-button wrappers. The 'Loaded' flag is set so the
// per-layer first-toggle lazy fetch doesn't fire a second time on top
// of the user's manual refresh.
export function refreshFires(map?: MlMap | null) { return refreshFiresInBbox(map); }
export function refreshSua(map?: MlMap | null) {
  // Manual refresh is allowed even if SUA hasn't been auto-loaded yet
  // — the user is explicitly asking for it.
  markOverlayLoaded('sua', true);
  return refreshSuaInBbox(map);
}
export function refreshGfaPublic(map?: MlMap | null) {
  markOverlayLoaded('gfa', true);
  return refreshGfa(map);
}
export function refreshCwaPublic(map?: MlMap | null) {
  markOverlayLoaded('cwa', true);
  return refreshCwa(map);
}
export function refreshFlightCatPublic(map?: MlMap | null) {
  markOverlayLoaded('flightcat', true);
  return refreshFlightCat(map);
}
export function refreshPirepsPublic(map?: MlMap | null) {
  markOverlayLoaded('pireps', true);
  return refreshPireps(map);
}
export function refreshClassBCD(map?: MlMap | null) {
  markOverlayLoaded('classBCD', true);
  return refreshClassBCDInBbox(map);
}
export function refreshAirways(map?: MlMap | null) {
  markOverlayLoaded('airways', true);
  return refreshAirwaysInBbox(map);
}

// HI-1 step 1: register the public refresh wrappers into the
// RefreshableOverlayRegistry. LayerSheet will iterate the registry
// instead of hard-importing named functions in a follow-up PR.
// Today this runs alongside the existing named imports — the registry
// is purely additive.
registerRefreshableOverlay({ id: 'fires',     label: 'Fires',                 refresh: () => refreshFires() });
registerRefreshableOverlay({ id: 'tfr',       label: 'TFRs',                  refresh: () => refreshTfrs() });
registerRefreshableOverlay({ id: 'sua',       label: 'Special-use airspace',  refresh: () => refreshSua() });
registerRefreshableOverlay({ id: 'gfa',       label: 'G-AIRMET (GFA)',        refresh: () => refreshGfaPublic() });
registerRefreshableOverlay({ id: 'cwa',       label: 'Center weather advisories', refresh: () => refreshCwaPublic() });
registerRefreshableOverlay({ id: 'flightcat', label: 'Flight category',       refresh: () => refreshFlightCatPublic() });
registerRefreshableOverlay({ id: 'pireps',    label: 'PIREPs',                refresh: () => refreshPirepsPublic() });
registerRefreshableOverlay({ id: 'classBCD',  label: 'Class B/C/D',           refresh: () => refreshClassBCD() });
registerRefreshableOverlay({ id: 'airways',   label: 'Airways',               refresh: () => refreshAirways() });

function fitMapToRoute(
  map: MlMap,
  route: Array<{
    lat: number;
    lon: number;
    procedureTag?: { placeholder?: boolean };
  }>,
) {
  if (route.length < 2) return;
  let west = Infinity, south = Infinity, east = -Infinity, north = -Infinity;
  for (const p of route) {
    // Skip placeholder procedure points (altitude- or manual-terminator
    // legs with no resolvable lat/lon, stored as (0, 0)). Including those
    // would expand the bounding box to encompass the Gulf of Guinea — the
    // map would zoom out to show the planned route AND the Atlantic.
    if (p.procedureTag?.placeholder) continue;
    if (!Number.isFinite(p.lat) || !Number.isFinite(p.lon)) continue;
    if (p.lat === 0 && p.lon === 0) continue;
    if (p.lon < west) west = p.lon;
    if (p.lon > east) east = p.lon;
    if (p.lat < south) south = p.lat;
    if (p.lat > north) north = p.lat;
  }
  if (!Number.isFinite(west) || !Number.isFinite(east) ||
      !Number.isFinite(south) || !Number.isFinite(north)) {
    return;
  }
  // DIAG: symmetric padding to test whether asymmetric right-padding
  // (which compensated for the Plan panel covering the right ~26rem)
  // was the cause of the partial-render bug. If line renders end-to-end
  // with this version, the issue is asymmetric-padding interacting with
  // LineString tessellation in MapLibre 4.7.1.
  map.fitBounds(
    [[west, south], [east, north]],
    {
      padding: 80,
      duration: 600,
      maxZoom: 11,
    }
  );
}


// Map interactions (rubber-band route-leg drag + long-press) moved
// to src/map/mapInteractions.ts as part of the refactor pass.


function invalidatePushPlanMemo(): void { lastPushPlanKey = null; }

// F10 (2026-05-16 perf audit): pushPlan memoization key. The plan source
// only needs setData when the visible route content changes — if the user
// hasn't edited the plan and hasn't advanced to a new leg, the rebuilt
// FeatureCollection is byte-identical to the previous one and the
// densifyRouteForGreatCircle pass + setData are wasted CPU. The key
// itself lives in planMemoKey.ts so a unit test can exercise it without
// importing this whole module.
let lastPushPlanKey: string | null = null;

function pushPlan(map: MlMap) {
  const { route } = usePlanStore.getState();
  const src = safeGetSource<GeoJSONSource>(map, 'plan');
  if (!src) return;
  const fix = useGpsStore.getState().fix;
  const { activeLegIndex } = computeActiveLeg(fix, route);

  // Memoization key.
  const key = computePlanMemoKey(
    route as Array<{ ident: string; lat: number; lon: number; procedureTag?: Record<string, unknown> | null }>,
    activeLegIndex,
  );
  if (key === lastPushPlanKey) return;
  lastPushPlanKey = key;
  const features: GeoJSON.Feature[] = [];
  // Per-leg LineStrings (densified to follow great-circle), each
  // tagged with `legIndex` = the route-index of the FROM point.
  // The rubber-band drag handler reads this to know which leg the
  // user is grabbing when they touch the line.
  //
  // Procedure expansion: legs are tagged `kind: 'procedure'` when both
  // endpoints came from a CIFP procedure (rendered magenta), and
  // legs are SKIPPED entirely when either endpoint is a placeholder
  // (altitude- or manual-terminator with no usable lat/lon).
  //
  // Missed-approach legs (IAP only) carry an additional kind
  // 'procedure-missed' so we can render them dashed.
  type RoutePoint = (typeof route)[number] & {
    procedureTag?: {
      placeholder?: boolean;
      synthetic?: boolean;
      hiddenInPlanList?: boolean;
      routeType?: string;
      isMissedApproach?: boolean;
      isHoldingPattern?: boolean;
      holdInboundCourse?: number | null;
      holdLeg?: { kind: 'time'; minutes: number } | { kind: 'distance'; nm: number } | null;
      holdTurn?: 'L' | 'R' | null;
      legType?: string;
      rfCenterLat?: number | null;
      rfCenterLon?: number | null;
      isFaf?: boolean;
      isMap?: boolean;
      isIaf?: boolean;
      isStepdownFix?: boolean;
      isFlyOver?: boolean;
      altitudeConstraint?: '+' | '-' | 'B' | 'at' | 'recommended' | null;
      altitude?: string | null;
      altitude2?: string | null;
    };
  };
  for (let i = 0; i < route.length - 1; i++) {
    const a = route[i] as RoutePoint;
    const b = route[i + 1] as RoutePoint;
    if (a.procedureTag?.placeholder || b.procedureTag?.placeholder) continue;
    const isProcedureLeg = !!(a.procedureTag && b.procedureTag
      && a.procedureTag.routeType === b.procedureTag.routeType);
    // A leg is a missed-approach leg when the DESTINATION endpoint is
    // tagged. (Origin can be the MAP itself which is the first leg
    // of the missed segment.)
    const isMissedLeg = isProcedureLeg && !!b.procedureTag?.isMissedApproach;
    // A leg is synthetic when EITHER endpoint was projected (alt-term
    // synthesis). Renders dashed regardless of missed-approach status.
    const isSyntheticLeg = isProcedureLeg && (a.procedureTag?.synthetic || b.procedureTag?.synthetic);

    // RF (Radius-to-Fix) legs: B's procedureTag describes the leg type
    // and carries the center fix. When all three are present we render
    // a true constant-radius arc instead of a great-circle straight
    // line. Fall back to great circle if center is missing.
    let coordinates: Array<[number, number]>;
    if (
      isProcedureLeg
      && b.procedureTag?.legType === 'RF'
      && b.procedureTag.rfCenterLat != null
      && b.procedureTag.rfCenterLon != null
    ) {
      coordinates = rfArcCoords({
        startLat: a.lat, startLon: a.lon,
        endLat: b.lat,   endLon: b.lon,
        centerLat: b.procedureTag.rfCenterLat,
        centerLon: b.procedureTag.rfCenterLon,
        turn: b.procedureTag.holdTurn === 'L' ? 'L' : 'R',
      }, 24);
    } else {
      coordinates = densifyRouteForGreatCircle([a, b], 25);
    }

    // Active-leg tagging: derive from GPS fix vs route progression.
    // 'flown' = behind us, 'active' = the leg we're currently on,
    // 'unflown' = ahead. Used by paint layers to color the line
    // (light blue ahead, orange active, dim flown).
    let legState: 'flown' | 'active' | 'unflown';
    if (activeLegIndex < 0)             legState = 'unflown';
    else if (i < activeLegIndex)        legState = 'flown';
    else if (i === activeLegIndex)      legState = 'active';
    else                                legState = 'unflown';
    features.push({
      type: 'Feature',
      properties: {
        legIndex: i,
        fromIdent: a.ident,
        toIdent: b.ident,
        kind: isMissedLeg ? 'procedure-missed'
             : isSyntheticLeg ? 'procedure-synthetic'
             : isProcedureLeg ? 'procedure'
             : 'enroute',
        procRouteType: isProcedureLeg ? (a.procedureTag!.routeType ?? null) : null,
        legState,
      },
      geometry: {
        type: 'LineString',
        coordinates,
      },
    });
  }
  for (const p of route as RoutePoint[]) {
    if (p.procedureTag?.placeholder) continue;
    // Hide synthesized altitude-terminator helpers from the visible
    // dot layer (they exist only to drive the dashed climb-line
    // geometry). Without this, a magenta dot would appear at the
    // synthetic endpoint with ident "VA@3443ft" — confusing on the
    // map and clickable as a phantom waypoint.
    if (p.procedureTag?.hiddenInPlanList) continue;
    let pointKind = 'enroute';
    if (p.procedureTag) {
      pointKind = p.procedureTag.isMissedApproach ? 'procedure-missed'
                : p.procedureTag.synthetic ? 'procedure-synthetic'
                : 'procedure';
    }
    // Decide which procedure-role icon to render at this fix (if any):
    // FAF → Maltese cross, IAF → open circle, MAP → filled square.
    // The plain magenta dot still renders for every procedure point
    // via the existing plan-points layer; these symbols stack on top.
    let procRole: 'faf' | 'iaf' | 'map' | null = null;
    if (p.procedureTag) {
      if (p.procedureTag.isFaf) procRole = 'faf';
      else if (p.procedureTag.isMap) procRole = 'map';
      else if (p.procedureTag.isIaf) procRole = 'iaf';
    }
    // Format altitude-constraint label for the fix.
    const altLabel = p.procedureTag?.altitudeConstraint
      ? formatAltitudeLabel(
          p.procedureTag.altitudeConstraint,
          p.procedureTag.altitude ?? null,
          p.procedureTag.altitude2 ?? null,
        )
      : null;
    // 2026-05-17: stamp the ORIGINAL feature kind on every plan-point
    // so pickFeaturesAt can route a click to the right detail card.
    // Without `planKind`, in-plan airports (KSTS, O46) get classified
    // as "fix" in the plan-points branch of pickFeaturesAt and the
    // user gets a lightweight DetailPane instead of the full
    // AirportDetail sheet.
    const planKind: 'airport' | 'fix' | 'navaid' | 'user' =
      p.kind === 'airport' ? 'airport'
      : p.kind === 'vor' || p.kind === 'ndb' || p.kind === 'navaid' ? 'navaid'
      : p.kind === 'user' ? 'user'
      : 'fix';
    features.push({
      type: 'Feature',
      properties: {
        ident: p.ident,
        kind: pointKind,
        planKind,
        procRole,
        altLabel,
      },
      geometry: { type: 'Point', coordinates: [p.lon, p.lat] },
    });
  }
  src.setData({ type: 'FeatureCollection', features });

  // Holding-pattern racetracks: each hold-marked procedure waypoint
  // emits a closed-loop LineString centered on the fix.
  const holdSrc = map.getSource('plan-holdings') as GeoJSONSource | undefined;
  if (holdSrc) {
    const holdFeatures: GeoJSON.Feature[] = [];
    for (const p of route as RoutePoint[]) {
      const tag = p.procedureTag;
      if (!tag?.isHoldingPattern) continue;
      if (tag.holdInboundCourse == null) continue;
      if (p.lat === 0 && p.lon === 0) continue; // placeholder w/o coords
      const coords = holdingRacetrackCoords({
        fixLat: p.lat,
        fixLon: p.lon,
        inboundCourseDeg: tag.holdInboundCourse,
        turnDirection: tag.holdTurn ?? null,
        leg: tag.holdLeg ?? null,
      });
      holdFeatures.push({
        type: 'Feature',
        properties: { ident: p.ident },
        geometry: { type: 'LineString', coordinates: coords },
      });
    }
    holdSrc.setData({ type: 'FeatureCollection', features: holdFeatures });
  }

  // Suppress the generic fix-layer diamond at every named fix that's
  // currently part of a loaded procedure on the active route. Without
  // this, turning on the 'fixes' layer paints a blue diamond directly
  // over each magenta procedure waypoint (and the dot under it).
  applyProcedureFixSuppression(map, route as RoutePoint[]);

  // Always re-raise plan layers to the top after pushing data. Overlay
  // refreshes (TFRs, fires, etc.) can add new layers above plan-* and
  // visually bury the active flight plan; the pilot's route line must
  // always be the topmost visual element.
  raisePlanLayersToTop(map);
}

/** Build the MapLibre filter that hides every named-fix diamond whose
 *  ident currently appears as a procedure waypoint on the active route.
 *  Runway-threshold idents like 'RW28L' aren't in the NASR fix table
 *  anyway so they're skipped. */
function applyProcedureFixSuppression(
  map: MlMap,
  route: ReadonlyArray<{ ident: string; procedureTag?: { placeholder?: boolean } }>,
): void {
  if (!map.getLayer('fixes-icon')) return;
  const suppressed = new Set<string>();
  for (const p of route) {
    if (!p.procedureTag) continue;
    if (p.procedureTag.placeholder) continue;
    if (!p.ident) continue;
    // Skip runway-end markers (they're never in NASR fixes anyway).
    if (/^RW\d+[LRC]?$/.test(p.ident)) continue;
    suppressed.add(p.ident);
  }
  const idents = Array.from(suppressed);
  try {
    if (idents.length === 0) {
      // Reset to no filter so all fixes paint.
      map.setFilter('fixes-icon', null);
    } else {
      map.setFilter('fixes-icon', [
        '!', ['in', ['get', 'ident'], ['literal', idents]],
      ]);
    }
  } catch {
    /* style mid-rebuild — next pushPlan will retry */
  }
}

/** Plan-related layers in render order (bottom → top). Listed here so
 *  raisePlanLayersToTop() iterates in a stable, predictable order and
 *  the result matches the layer-creation order. */
const PLAN_LAYER_IDS: ReadonlyArray<string> = [
  'plan-holding-glow',
  'plan-holding-line',
  'plan-line-glow',
  'plan-line-border',
  'plan-line',
  'plan-line-procedure-glow',
  'plan-line-procedure-border',
  'plan-line-procedure',
  'plan-line-procedure-missed-glow',
  'plan-line-procedure-missed',
  'plan-line-procedure-synthetic',
  'plan-points',
  'plan-role-icons',
  'plan-alt-labels',
  'plan-labels',
];

/** Move every plan-* layer to the top of the layer stack. Safe to call
 *  on every pushPlan and after every overlay refresh; the moveLayer
 *  operation is a no-op when the layer is already on top. */
function raisePlanLayersToTop(map: MlMap): void {
  for (const id of PLAN_LAYER_IDS) {
    if (map.getLayer(id)) {
      try { map.moveLayer(id); } catch { /* style mid-rebuild */ }
    }
  }
}

// (Track helpers extracted to src/map/controllers/TrackController.tsx)

// Latest in-flight bbox queries; we abort old ones when the viewport changes.
let bboxAbort: {
  airports?: AbortController;
  fixes?: AbortController;
  navaids?: AbortController;
  heliports?: AbortController;
} = {};

/**
 * HI-1' (2026-05-24): Replaces the bulk of old refreshBboxLayers.
 *
 * Handles the core fetchBbox calls for airports / fixes / navaids /
 * heliports — these use the bboxAbort pattern and aren't owned by any
 * layer module's bboxRefresh. All other overlay refreshes are now
 * owned by the layer modules via LayerFamilySpec.bboxRefresh, called
 * from runBboxRefreshHandlers() in the moveend handler.
 */
async function refreshBboxCoreFeatures(map: MlMap, zoom?: number) {
  const z = zoom ?? Math.round(map.getZoom());
  const b = map.getBounds();
  const args = { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth(), zoom: z };

  // Airports
  bboxAbort.airports?.abort();
  bboxAbort.airports = new AbortController();
  fetchBbox({ ...args, layer: 'airports', limit: 400, signal: bboxAbort.airports.signal })
    .then((items) => pushAirports(map, items, z))
    .catch((e) => { if (e?.name !== 'AbortError') console.warn('bbox airports', e); });

  // Fixes (server gates at z≥8; client sends all zooms)
  bboxAbort.fixes?.abort();
  bboxAbort.fixes = new AbortController();
  fetchBbox({ ...args, layer: 'fixes', limit: 400, signal: bboxAbort.fixes.signal })
    .then((items) => pushFixes(map, items))
    .catch((e) => { if (e?.name !== 'AbortError') console.warn('bbox fixes', e); });

  // Navaids
  bboxAbort.navaids?.abort();
  bboxAbort.navaids = new AbortController();
  fetchBbox({ ...args, layer: 'navaids', limit: 200, signal: bboxAbort.navaids.signal })
    .then((items) => pushNavaids(map, items))
    .catch((e) => { if (e?.name !== 'AbortError') console.warn('bbox navaids', e); });

  // Heliports: refresh on viewport change when user has opted in
  // AND zoom is adequate. Server gates at z≥8 but fetching at z<8
  // returns only Alaska pads which is wasteful.
  if (heliportsLoaded && map.getZoom() >= 8) void refreshHeliportsInBbox(map);
}

async function refreshHeliportsInBbox(map?: MlMap | null) {
  if (!map) return;
  const z = Math.round(map.getZoom());
  // 2026-05-16: zoom threshold dropped 10 -> 8 to match server-side and
  // client-layer minzoom updates per boss feedback.
  if (z < 8) {
    // Don't fetch when out of zoom range — also clear the source so a
    // pan-and-zoom-back combo doesn't show stale results.
    const src = safeGetSource<GeoJSONSource>(map, 'heliports');
    if (src) src.setData(EMPTY_FC);
    return;
  }
  const b = map.getBounds();
  const args = { west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth(), zoom: z };
  bboxAbort.heliports?.abort();
  bboxAbort.heliports = new AbortController();
  // Read the hospital-only filter live from the store — this runs
  // outside the React render tree (called on pan/zoom + visibility
  // change), so we use getState() rather than a hook.
  const hospOnly = useUiStore.getState().heliportsHospitalsOnly === true;
  try {
    const items = await fetchBbox({
      ...args,
      layer: 'heliports',
      limit: 400,
      hospitalsOnly: hospOnly,
      signal: bboxAbort.heliports.signal,
    });
    const src = safeGetSource<GeoJSONSource>(map, 'heliports');
    if (!src) return;
    src.setData({
      type: 'FeatureCollection',
      features: items.map((h) => ({
        type: 'Feature',
        properties: {
          ident: h.ident,
          name: h.name,
          type: h.type,
          // Carry the hospital flag onto the map feature so the
          // icon expression can style hospital pads with a tinted
          // glyph in a future iteration. Defaults to false when
          // the server omitted the field (back-compat).
          hospital: (h as { hospital?: boolean }).hospital === true,
        },
        geometry: { type: 'Point', coordinates: [h.lon, h.lat] },
      })),
    });
  } catch (e: any) {
    if (e?.name !== 'AbortError') console.warn('bbox heliports', e);
  }
}

/**
 * Fetch the FAA WeatherCams site list and paint the wxcams source.
 * Single global fetch (the list is small and rarely changes) so we
 * don't bother with a bbox endpoint or AbortController.
 */
// refreshWxCams moved to src/map/layers/wxcams.ts (HI-1' 2026-05-24).
// wxcams.onToggleOn calls it on first toggle-on.

function pushFixes(map: MlMap, items: BboxFix[]) {
  const src = safeGetSource<GeoJSONSource>(map, 'fixes');
  if (!src) return;
  src.setData({
    type: 'FeatureCollection',
    features: items.map((f) => ({
      type: 'Feature',
      properties: { ident: f.ident, cat: f.cat, use: f.use },
      geometry: { type: 'Point', coordinates: [f.lon, f.lat] },
    })),
  });
}

function pushAirports(map: MlMap, items: BboxAirport[], _z: number) {
  const src = safeGetSource<GeoJSONSource>(map, 'airports');
  if (!src) return;
  src.setData({
    type: 'FeatureCollection',
    features: items.map((a) => ({
      type: 'Feature',
      properties: {
        ident: a.ident,
        type: a.type,
        name: a.name,
        // Forward the NASR-truth flags so AIRPORT_ICON_EXPRESSION can
        // pick the right symbol per airport. Missing flags fall back to
        // the type/scheduled heuristic in the expression.
        hasTower: a.hasTower === true,
        hasFuel: a.hasFuel === true,
        fuelTypes: a.fuelTypes ?? [],
        lighted: a.lighted === true,
        pavedFt: a.pavedFt ?? 0,
        scheduled: a.scheduled === true,
      },
      geometry: { type: 'Point', coordinates: [a.lon, a.lat] },
    })),
  });
}

function pushNavaids(map: MlMap, items: BboxNavaid[]) {
  const src = safeGetSource<GeoJSONSource>(map, 'waypoints');
  if (!src) return;
  src.setData({
    type: 'FeatureCollection',
    features: items.map((n) => ({
      type: 'Feature',
      properties: { ident: n.ident, type: n.type, name: n.name },
      geometry: { type: 'Point', coordinates: [n.lon, n.lat] },
    })),
  });
}

// ownshipSvg() removed — marker rendering lives in ./ownshipMarkerSvg.ts
// and is driven by the active aircraft profile's ownshipMarker field.
// See the Ownship effect above (2026-05-12).
